import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, FileText } from 'lucide-react';
import { useAuth } from '../../../AuthContext';
import configData from '../../../configure';
import { fetchMailboxItems } from '../../../services/resourceService';
import { stripHtml } from '../../../utils/textUtils';
import { fetchAllAttendanceRecords } from '../../../services/attendanceService';
import Table from '../../../components/Table';
import { MiniLoader } from '../../../components/Common/LoadingScreen';
import EmptyState from '../../../components/Common/SafeApp/EmptyState';


export const StudentInfoCard = () => {
  const { user, studentInfo, orgId, axios } = useAuth();

  const getFullName = () => {
    if (!user) return 'N/A';
    const first = user.firstName || '';
    const middle = user.middleName || '';
    const last = user.lastName || '';
    return `${first} ${middle ? `${middle} ` : ''}${last}`.trim() || 'Unknown Student';
  };

  const name = getFullName();
  const role = user?.userType || 'Student';
  const admissionId = studentInfo?.admissionId || 'ADM-N/A';
  const rollNumber = studentInfo?.rollNumber || 'N/A';
  const dob = studentInfo?.studentDob ? new Date(studentInfo.studentDob).toLocaleDateString('en-IN') : 'N/A';
  const gender = studentInfo?.gender || 'N/A';
  const email = user?.email || 'N/A';
  const phone = user?.phoneNumber || 'N/A';

  // State for class and section info
  const [classInfo, setClassInfo] = useState({ className: 'N/A', sectionName: 'N/A' });

  useEffect(() => {
    if (!orgId || !studentInfo?.classId) return;
    axios
      .get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`)
      .then((resp) => {
        const classes = Array.isArray(resp.data) ? resp.data : (resp.data?.items || resp.data?.content || []);
        const current = classes.find((c) => c?.id === studentInfo?.classId || c?.classId === studentInfo?.classId);
        if (current) {
          setClassInfo({
            className: current.className || current.name || 'N/A',
            sectionName: current.sectionName || current.section || 'N/A',
          });
        }
      })
      .catch((err) => {
        console.error('Failed to fetch class info:', err);
      });
  }, [orgId, studentInfo?.classId]);

  return (
    <div className="profile-info-card sd-card sd-student-info">
      <div className="profile-info-header">
        <div className="profile-info-avatar" style={{ background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' }}>
          {user?.firstName ? user.firstName.charAt(0).toUpperCase() : 'S'}
          <div className="avatar-status-dot"></div>
        </div>
        <div className="profile-info-identity">
          <div className="identity-top">
            <span className="profile-role-badge" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}>
              {role}
            </span>
          </div>
          <h2 className="profile-name">{name}</h2>
          <p className="profile-id-tag">Adm ID: {admissionId}</p>
        </div>
      </div>

      <div className="profile-info-grid">
        <div className="info-grid-item">
          <label>Roll Number</label>
          <span>{rollNumber}</span>
        </div>
        <div className="info-grid-item">
          <label>Date of Birth</label>
          <span>{dob}</span>
        </div>
        <div className="info-grid-item">
          <label>Gender</label>
          <span>{gender}</span>
        </div>
        <div className="info-grid-item">
          <label>Email Address</label>
          <span title={email}>{email}</span>
        </div>
        <div className="info-grid-item">
          <label>Phone Number</label>
          <span>{phone}</span>
        </div>
        <div className="info-grid-item">
          <label>Class</label>
          <span>{classInfo.className}</span>
        </div>
        <div className="info-grid-item">
          <label>Section</label>
          <span>{classInfo.sectionName}</span>
        </div>
        </div>
      </div>
    
  );
};

export const GpaPerformanceCard = ({ userId }) => {
  const { axios, token, orgId } = useAuth();
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [eventNameMap, setEventNameMap] = useState({});

  const { data: performanceData, isLoading } = useQuery({
    queryKey: ['studentDashboardGpa', userId, orgId],
    queryFn: async () => {
      if (!userId || !orgId) return [];
      const response = await axios.get(
        configData.get_resources_resources_student_performance_user_pagesize_20(userId),
        {
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.items || [];
    },
    enabled: !!userId && !!orgId && !!token,
  });

  useEffect(() => {
    if (!performanceData?.length || !orgId) return;

    const fetchEvents = async () => {
      try {
        const response = await axios.get(configData.RESOURCES_EVENTS, {
          params: {
            orgId,
            pageSize: 200,
            eventStartDate: '2024-01-01T00:00:00Z',
            eventEndDate: '2030-12-31T23:59:59Z',
          },
        });
        const map = {};
        (response.data.items || []).forEach((event) => {
          map[event.eventId] = event.eventName;
        });
        setEventNameMap(map);
      } catch (error) {
        console.error('Failed to fetch event names:', error);
      }
    };

    fetchEvents();
  }, [performanceData, orgId, axios]);

  const assessments = useMemo(() => {
    const list = Array.isArray(performanceData) ? performanceData : (performanceData?.items || []);
    if (!list.length) return [];
    return list.map((item, index) => ({
      id: item.studentPerformanceId,
      name:
        eventNameMap[item.eventId] ||
        item.eventName ||
        item.assessmentName ||
        (item.assessmentId && item.assessmentId.length < 30 ? item.assessmentId : null) ||
        `Assessment ${index + 1}`,
    }));
  }, [performanceData, eventNameMap]);

  useEffect(() => {
    if (assessments.length > 0 && !selectedAssessmentId) {
      setSelectedAssessmentId(assessments[0].id);
    }
  }, [assessments, selectedAssessmentId]);

  const currentGpa = useMemo(() => {
    if (!performanceData || !selectedAssessmentId) return '0.0';

    const assessment = (performanceData || []).find((item) => item?.studentPerformanceId === selectedAssessmentId);
    if (!assessment?.assessmentPerformanceList?.length) return '0.0';

    const totalObtained = assessment.assessmentPerformanceList.reduce((sum, item) => sum + Number(item.marksObtained || 0), 0);
    const totalMax = assessment.assessmentPerformanceList.reduce((sum, item) => sum + Number(item.maxMarks || 0), 0);
    if (totalMax === 0) return '0.0';

    return (((totalObtained / totalMax) * 100) / 10).toFixed(1);
  }, [performanceData, selectedAssessmentId]);

  return (
    <div className="sd-card sd-gpa-card">
      <div className="sd-card-header">
        <h3>GPA Performance</h3>
        <div className="sd-dropdown">
          <select
            value={selectedAssessmentId}
            onChange={(e) => setSelectedAssessmentId(e.target.value)}
            disabled={isLoading || assessments.length === 0}
          >
            {assessments.length > 0 ? (
              assessments.map((assessment) => (
                <option key={assessment.id} value={assessment.id}>{assessment.name}</option>
              ))
            ) : (
              <option>No Assessment</option>
            )}
          </select>
        </div>
      </div>
      <div className="sd-gpa-display">
        {isLoading ? (
          <div className="sd-gpa-circle">
            <span className="sd-gpa-number" style={{ fontSize: '18px' }}>...</span>
          </div>
        ) : (
          <div className="sd-gpa-circle">
            <span className="sd-gpa-number">{currentGpa}</span>
            <span className="sd-gpa-text">GPA</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const AttendanceCard = () => {
  const { user, studentInfo, token } = useAuth();
  const userId = studentInfo?.userId || user?.userId || user?.id;

  const { data: records, isLoading } = useQuery({
    queryKey: ['studentDashboardAttendance', userId],
    queryFn: () => {
      if (!userId) return [];
      return fetchAllAttendanceRecords({
        filterType: 'user',
        id: userId,
        startDate: `${new Date().getFullYear()}-01-01`,
        endDate: `${new Date().getFullYear()}-12-31`
      });
    },
    enabled: !!userId && !!token,
  });

  const percentage = useMemo(() => {
    const list = Array.isArray(records) ? records : (records?.items || records?.content || []);
    if (!list || list.length === 0) return 100;
    
    let attendedCount = 0;
    list.forEach(r => {
      if (r?.attendanceStatus === 'PRESENT' || r?.attendanceStatus === 'LATE' || r?.status === 'PRESENT' || r?.status === 'LATE') {
        attendedCount++;
      }
    });
    
    return Math.round((attendedCount / list.length) * 100);
  }, [records]);

  return (
    <div className="sd-card sd-attendance-card">
      <div className="sd-card-header">
        <h3>Overall Attendance</h3>
      </div>
      <div className="sd-attendance-display">
        <div className="sd-attendance-circle">
          {isLoading ? (
            <span className="sd-attendance-percentage" style={{ fontSize: '18px' }}>...</span>
          ) : (
            <span className="sd-attendance-percentage">{percentage}%</span>
          )}
        </div>
        <div className="sd-attendance-legend">
          <div className="sd-legend-item">
            <span className="sd-legend-color sd-present"></span>
            <span>Attended</span>
          </div>
          <div className="sd-legend-item">
            <span className="sd-legend-color sd-absent"></span>
            <span>Missed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RecentMessagesCard = () => {
  const { user, token, orgId } = useAuth();
  const navigate = useNavigate();
  const userId = user?.userId;

  const { data, isLoading } = useQuery({
    queryKey: ['studentDashboardMessages', userId, orgId],
    queryFn: () => fetchMailboxItems({ userId, orgId, size: 3 }),
    enabled: !!userId && !!orgId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  const recentMessages = useMemo(() => {
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  }, [data]);

  return (
    <div className="sd-card sd-notifications-card">
      <div className="sd-card-header">
        <h3>Recent Messages</h3>
        <span className="sd-notification-icon" onClick={() => navigate('/student-inbox')} style={{ cursor: 'pointer' }}>🔔</span>
      </div>
      <div className="sd-notification-list">
        {isLoading ? (
          <MiniLoader message="Fetching Messages" />
        ) : !Array.isArray(recentMessages) || recentMessages.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No messages"
            description="Your student inbox is healthy and empty."
            containerHeight="160px"
          />
        ) : (
          recentMessages.map((message, index) => (
            <div
              key={index}
              className="sd-notification-item"
              onClick={() => navigate('/student-inbox')}
              style={{ cursor: 'pointer', padding: '12px', borderBottom: '1px solid #f1f1f1' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600', fontSize: '13px' }}>
                  {stripHtml(message.participantSummary || message.senderName) || 'Notification'}
                </span>
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>{new Date(message.lastMessageAt || message.createdAt).toLocaleDateString()}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {stripHtml(message.lastMessagePreview || message.subject || 'New Message')}
              </div>
            </div>
          ))
        )}
      </div>
      <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>
        <button onClick={() => navigate('/student-inbox')} className="sd-view-all-btn">
          View All Messages
        </button>
      </div>
    </div>
  );
};

export const DocumentsCard = () => {
  const { axios, token, user, studentInfo } = useAuth();
  const [documents, setDocuments] = useState([]);

  const userId = studentInfo?.userId || user?.id || user?.userId;

  const formatDocumentType = (type) => {
    if (!type) return 'Other';
    return type
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const fetchDocuments = async () => {
    if (!userId) {
      setDocuments([]);
      return;
    }

    try {
      const authHeader = token && token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const response = await axios.get(
        `${configData.CAMPUS_FILE_GET_BY_ENTITY}/${userId}?entityType=USER&generateDownloadUrls=true`,
        {
          headers: {
            Authorization: authHeader,
          },
        }
      );
      const fileList =
        Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.items)
            ? response.data.items
            : Array.isArray(response.data?.data)
              ? response.data.data
              : [];
      setDocuments(fileList);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      accessor: 'originalFileName',
      header: 'File Name',
      render: (row) => row.originalFileName || 'Unnamed File',
    },
    {
      accessor: 'fileCategory',
      header: 'Category',
      render: (row) => formatDocumentType(row.fileCategory || 'OTHER'),
    },
    {
      accessor: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="action-buttons">
          <a
            href={row.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="view-button"
          >
            View
          </a>
        </div>
      ),
    },
  ];

  return (
    <div className="sd-card sd-documents-card">
      <div className="sd-card-header">
        <h3>Latest Published Docs</h3>
      </div>
      {!documents.length ? (
        <EmptyState
          icon={FileText}
          title="No documents"
          description="Your published academic documents will appear here."
          containerHeight="240px"
        />
      ) : (
        <Table
          data={documents}
          columns={columns}
          pagination
          pageSize={5}
          emptyMessage="No documents available."
        />
      )}
      <style jsx>{`
        .sd-documents-card {
          padding: 20px;
        }
        .action-buttons {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .view-button {
          display: inline-block;
          padding: 6px 12px;
          background-color: #4a90e2;
          color: white;
          border-radius: 4px;
          text-decoration: none;
          font-size: 0.9em;
        }
      `}</style>
    </div>
  );
};
